import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import PlayerLocationDialog from '../pages/components/PlayerLocationDialog';
import { useAlert } from '../alert/AlertContext';
import { isWorldAllowed } from '../pages/components/WorldSelectCombo';

const PlayerLocationContext = createContext();

const onGetLocation = async () => {
    const response = await axios.get(`/playerlocation`);
    const roundedLocation = {
        x: parseFloat(response.data.x).toFixed(2),
        y: parseFloat(response.data.y).toFixed(2),
        z: parseFloat(response.data.z).toFixed(2),
        world: response.data.world,
        rotationY: parseFloat(response.data.rotationY).toFixed(2),
        rotationX: parseFloat(response.data.rotationX).toFixed(2),
    };
    return roundedLocation;
};

export const usePlayerLocation = (xzOnly = false, allowedWorlds = []) => {
    const { setShowLocationSelectDialog, setLocation } = useContext(PlayerLocationContext);

    const getLocation = useCallback(async () => {
        const promptForLocation = (message) => new Promise((resolve) => {
            const handleClose = (location) => {
                setLocation(location);
                setShowLocationSelectDialog({ open: false, onClose: null, xzOnly: false, message: null, allowedWorlds: [] });
                resolve(location);
            };

            setShowLocationSelectDialog({ open: true, onClose: handleClose, xzOnly: xzOnly, message: message, allowedWorlds: allowedWorlds });
        });

        let location;
        try {
            location = await onGetLocation();
        } catch (error) {
            return promptForLocation(error.response.data);
        }

        if (!isWorldAllowed(location.world, allowedWorlds)) {
            return promptForLocation(`World ${location.world} is not allowed here`);
        }

        setLocation(location);
        return location;
    }, [xzOnly, allowedWorlds]);

    return getLocation;
};

export const PlayerLocationProvider = ({ children }) => {
    const [location, setLocation] = useState(null);
    const [showLocationSelectDialog, setShowLocationSelectDialog] = useState({ open: false, onClose: null, xzOnly: false, message: null });

    const alert = useAlert();

    const handleLocationSet = (location) => {
        if (showLocationSelectDialog.onClose) {
            showLocationSelectDialog.onClose(location);
        }
    };

    const foo = useMemo(() => ({location, setLocation, setShowLocationSelectDialog, alert}), []);

    return (
        <PlayerLocationContext.Provider value={foo}>
            {children}
            {showLocationSelectDialog.open && (
                <PlayerLocationDialog
                    onSelect={handleLocationSet}
                    onClose={() => setShowLocationSelectDialog({ open: false, onClose: null, xzOnly: false })}
                    defaultLocation={location}
                    xzOnly={showLocationSelectDialog.xzOnly}
                    errorMessage={showLocationSelectDialog.message}
                    allowedWorlds={showLocationSelectDialog.allowedWorlds}
                />
            )}
        </PlayerLocationContext.Provider>
    );
};
